import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing } from '../services/api';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import SliderSignupModal from './SliderSignupModal';

// Public homepage banner slider — replaces the static "bakery" hero image.
// Banners come from /api/marketing/homepage-banners which:
//   - matches signed-in surveyed users by their interest categories
//   - shuffles randomly for guests
// Auto-rotates every 6 seconds; users can navigate manually.
export default function HomepageBannerSlider() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [signupBiz, setSignupBiz] = useState(null); // open modal for this biz
  const tRef = useRef(null);

  useEffect(() => {
    marketing.banners().then(r => setItems(r || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    tRef.current = setInterval(() => setIdx(i => (i + 1) % items.length), 6000);
    return () => clearInterval(tRef.current);
  }, [items.length]);

  if (items.length === 0) return null;   // Parent still shows the static hero if none.

  const b = items[idx];
  const go = (n) => setIdx((n + items.length) % items.length);

  return (
    <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-neutral-100 aspect-[16/9] max-w-3xl mx-auto"
      data-testid="homepage-banner-slider">
      <img src={b.heroImage} alt={b.headline} className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display='none'; }}/>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"/>
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
        <div className="text-[10px] tracking-widest uppercase opacity-80">Sponsored by {b.businessName}</div>
        <div className="text-lg md:text-2xl font-black leading-tight mt-1" data-testid="banner-headline">{b.headline}</div>
        {b.subheadline && <div className="text-xs md:text-sm opacity-90 mt-0.5">{b.subheadline}</div>}
        <Link to={b.ctaUrl} onClick={() => marketing.trackBannerClick(b.id)}
          data-testid={`banner-cta-${b.slug}`}
          className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-400 text-white text-xs font-semibold">
          {b.ctaText || 'See more'} <ArrowRight size={12}/>
        </Link>
        {b.signupFormEnabled && b.businessId && (
          <label
            className="mt-2 flex items-center gap-2 text-[11px] text-white/90 cursor-pointer select-none"
            data-testid={`banner-signup-opt-${b.slug}`}>
            <input
              type="checkbox"
              checked={false}
              onChange={(e) => { if (e.target.checked) setSignupBiz({ id: b.businessId, name: b.businessName }); }}
              className="accent-green-500 w-3.5 h-3.5"
            />
            <span>Send me deals from <b>{b.businessName}</b></span>
          </label>
        )}
      </div>
      {items.length > 1 && (
        <>
          <button type="button" onClick={() => go(idx - 1)} data-testid="banner-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2">
            <ChevronLeft size={16}/>
          </button>
          <button type="button" onClick={() => go(idx + 1)} data-testid="banner-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2">
            <ChevronRight size={16}/>
          </button>
          <div className="absolute bottom-2 right-2 flex gap-1">
            {items.map((_, i) => (
              <button key={i} type="button" onClick={() => setIdx(i)}
                data-testid={`banner-dot-${i}`}
                className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`}/>
            ))}
          </div>
        </>
      )}
      {signupBiz && (
        <SliderSignupModal
          businessId={signupBiz.id}
          businessName={signupBiz.name}
          onClose={() => setSignupBiz(null)}
        />
      )}
    </div>
  );
}
